import {Dialog, DialogContent, IconButton, Box, useMediaQuery, useTheme} from "@mui/material";
import {Close as CloseIcon} from "@mui/icons-material";
import {useRef} from "react";

// Helper to detect video type
const getVideoType = (url) => {
    if (!url) return null;
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('vimeo.com')) return 'vimeo';
    if (url.includes('.m3u8')) return 'hls';
    if (url.includes('.mp4')) return 'mp4';
    if (url.includes('.webm')) return 'webm';
    return 'unknown';
};

// Extract YouTube video ID
const getYouTubeId = (url) => {
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^#&?]*)/,
        /youtube\.com\/embed\/([^#&?]*)/,
        /youtube\.com\/shorts\/([^#&?]*)/
    ];
    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) return match[1];
    }
    return null;
};

// Extract Vimeo video ID
const getVimeoId = (url) => {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? match[1] : null;
};

const VideoPlayerModal = ({isOpen, close, videoUrl, thumbnailUrl}) => {
    const videoRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!isOpen) return null;

    const videoType = getVideoType(videoUrl);

    const renderPlayer = () => {
        switch (videoType) {
            case 'youtube': {
                const youtubeId = getYouTubeId(videoUrl);
                return (
                    <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${youtubeId}?autoplay=0&rel=0`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="YouTube video"
                    />
                );
            }
            case 'vimeo': {
                const vimeoId = getVimeoId(videoUrl);
                return (
                    <iframe
                        width="100%"
                        height="100%"
                        src={`https://player.vimeo.com/video/${vimeoId}`}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title="Vimeo video"
                    />
                );
            }
            case 'hls':
            case 'mp4':
            case 'webm':
            default:
                return (
                    <video
                        ref={videoRef}
                        controls
                        poster={thumbnailUrl || undefined}
                        style={{width: '100%', height: '100%', backgroundColor: '#000'}}
                        src={videoUrl}
                    >
                        Your browser does not support the video tag.
                    </video>
                );
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={close}
            maxWidth={isMobile ? "xs" : "lg"}
            fullWidth
            fullScreen={isMobile}
            sx={{
                "& .MuiDialog-paper": {
                    borderRadius: isMobile ? 0 : "14px",
                    backgroundColor: "#000",
                    overflow: "hidden",
                    margin: isMobile ? 0 : undefined,
                    maxHeight: isMobile ? "100%" : undefined
                }
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: isMobile ? 12 : 8,
                    right: isMobile ? 12 : 8,
                    zIndex: 10
                }}
            >
                <IconButton
                    onClick={close}
                    sx={{
                        color: "#fff",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.7)"
                        }
                    }}
                >
                    <CloseIcon/>
                </IconButton>
            </Box>
            <DialogContent
                sx={{
                    padding: 0,
                    backgroundColor: "#000",
                    height: isMobile ? "100vh" : "70vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                {renderPlayer()}
            </DialogContent>
        </Dialog>
    );
};

export default VideoPlayerModal;